import { Fragment, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { sortBy } from "lodash";
import Label from "./FormLabel";
import HelperText from "./HelperText";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export enum SimpleDropdownLabel {
  Type = "Type",
  Industry = "Industry",
  Sort = "Sort",
}

interface SimpleDropdownProps {
  options: { id: number | string; name: string }[];
  label: SimpleDropdownLabel;
  onChange: (selected: string) => void;
  value: string | number;
  helperText?: string;
  theme?: string;
  returnType?: "id" | "name";
}

export default function SimpleDropdown({
  options,
  label,
  onChange,
  value,
  helperText,
  theme,
  returnType = "name",
}: SimpleDropdownProps) {
  const defaultOption = { id: null, name: "Please select" };

  const initialValue =
    returnType === "id"
      ? options.find((option) => option.id === value) || defaultOption
      : options.find((option) => option.name === value) || defaultOption;

  if (initialValue === undefined || initialValue === null) {
    const errorMessage = `SimpleDropdown is uncontrolled: The initialValue is [${initialValue}] for returnType [${returnType}], value [${value}], label [${label}], and helperText [${helperText}].`;
    console.error(errorMessage);
  }

  const sortedOptions = [defaultOption, ...sortBy(options, ["name"])];
  const [selected, setSelected] = useState(initialValue);

  const handleSelect = (selected) => {
    setSelected(selected);
    onChange(
      selected.id === null
        ? null
        : returnType === "id"
        ? selected.id
        : selected.name
    );
  };

  return (
    <Listbox value={selected} onChange={handleSelect}>
      {({ open }) => (
        <>
          <div className="relative mt-1">
            <Label label={label} theme={theme} />
            <Listbox.Button className="simple-dropdown-button">
              <span className="block truncate">
                {selected?.name === undefined ? " " : selected?.name}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>
            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="simple-dropdown-container">
                {sortedOptions.map((option) => (
                  <Listbox.Option
                    key={option.id}
                    className={({ active }) =>
                      classNames(
                        active ? "bg-indigo-600 text-white" : "text-gray-900",
                        "relative cursor-default select-none py-2 pl-3 pr-9"
                      )
                    }
                    value={option}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={classNames(
                            selected ? "font-semibold" : "font-normal",
                            "block truncate"
                          )}
                        >
                          {option.name}
                        </span>

                        {selected ? (
                          <span
                            className={classNames(
                              active ? "text-white" : "text-indigo-600",
                              "absolute inset-y-0 right-0 flex items-center pr-4"
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
            {helperText && <HelperText helperText={helperText} />}
          </div>
        </>
      )}
    </Listbox>
  );
}
